import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/Button";
import { InputField, SelectField } from "../components/Field";
import { PageHeader } from "../components/PageHeader";
import { usePreferences } from "../app/PreferencesProvider";
import { useAuth } from "../app/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import { completeOnboarding } from "../services/profile";
import { ApiRequestError } from "../services/api";
import { isOnboardingComplete } from "../routes/guards";
import type { LengthUnit, VolumeUnit, WeightUnit } from "../types/models";
import { detectBrowserTimezone } from "../lib/timezone";
import { formatVolume, volumeToMl } from "../lib/units";
import { buildHydrationPreview } from "../lib/hydrationPreview";

export function OnboardingPage() {
  const { t, translateError } = useI18n();
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const { preferences, updatePreferences } = usePreferences();
  const [pending, setPending] = useState(false);
  const [formError, setFormError] = useState("");
  const [height, setHeight] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [reference, setReference] = useState<"female" | "male">("female");
  const [weight, setWeight] = useState("");
  const [customGoal, setCustomGoal] = useState("");

  const heightNumber = readPositiveNumber(height);
  const birthYearNumber = readBirthYear(birthYear);
  const weightNumber = readPositiveNumber(weight);
  const customGoalNumber = readPositiveNumber(customGoal);
  const preview = useMemo(
    () =>
      buildHydrationPreview({
        height: heightNumber,
        heightUnit: preferences.lengthUnit,
        birthYear: birthYearNumber,
        hydrationReference: reference,
        weight: weightNumber,
        weightUnit: preferences.weightUnit
      }),
    [birthYearNumber, heightNumber, preferences.lengthUnit, preferences.weightUnit, reference, weightNumber]
  );

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }
    const root = document.documentElement;
    const updateViewportTop = () => {
      root.style.setProperty("--visual-viewport-top", `${Math.max(0, viewport.offsetTop)}px`);
    };
    updateViewportTop();
    viewport.addEventListener("resize", updateViewportTop);
    viewport.addEventListener("scroll", updateViewportTop);
    window.addEventListener("focusin", updateViewportTop);
    window.addEventListener("focusout", updateViewportTop);
    return () => {
      viewport.removeEventListener("resize", updateViewportTop);
      viewport.removeEventListener("scroll", updateViewportTop);
      window.removeEventListener("focusin", updateViewportTop);
      window.removeEventListener("focusout", updateViewportTop);
      root.style.removeProperty("--visual-viewport-top");
    };
  }, []);

  const submitOnboarding = async (event: FormEvent) => {
    event.preventDefault();
    setFormError("");

    if (!heightNumber || !birthYearNumber || !preview) {
      setFormError(t("onboarding.completeRequired"));
      return;
    }

    setPending(true);
    try {
      const result = await completeOnboarding(
        {
          height: heightNumber,
          birthYear: birthYearNumber,
          hydrationReference: reference,
          preferredWeight: weightNumber ?? undefined,
          customGoalMl: customGoalNumber ? volumeToMl(customGoalNumber, preferences.volumeUnit) : preview.goalMl
        },
        preferences
      );
      if (!result.onboarding_completed) {
        setFormError(t("onboarding.refreshIncomplete"));
        return;
      }
      const currentUser = await refreshSession({ requireProfile: true });
      if (!currentUser || !isOnboardingComplete(currentUser)) {
        setFormError(t("onboarding.refreshIncomplete"));
        return;
      }
      void navigate("/today", { replace: true });
    } catch (error) {
      setFormError(formatOnboardingError(error, translateError, t));
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="main-content onboarding-page">
      <section className="onboarding-preview panel" aria-live="polite">
        <div>
          <span>{t("onboarding.estimate")}</span>
          <strong>{preview ? formatVolume(preview.goalMl, preferences.volumeUnit) : "-"}</strong>
        </div>
        {preview ? <p>{t("onboarding.estimateNote")}</p> : null}
      </section>
      <PageHeader title={t("onboarding.title")} eyebrow={t("onboarding.welcome")} />
      <section className="panel">
        <form className="form-grid onboarding-form" onSubmit={(event) => void submitOnboarding(event)}>
          {formError ? <div className="form-alert">{formError}</div> : null}
          <UnitNumberField
            id="onboarding-height"
            label={t("onboarding.height")}
            min={1}
            unit={preferences.lengthUnit}
            unitLabel={t("onboarding.lengthUnit")}
            value={height}
            onUnitChange={(unit) => updatePreferences({ lengthUnit: unit as LengthUnit })}
            onValueChange={setHeight}
            options={[
              { label: "cm", value: "cm" },
              { label: "inch", value: "inch" }
            ]}
            required
          />
          <InputField
            label={t("onboarding.birthYear")}
            type="number"
            min={1900}
            max={new Date().getFullYear()}
            value={birthYear}
            onChange={(event) => setBirthYear(event.target.value)}
            required
          />
          <div className="field">
            <span className="field__label">{t("onboarding.reference")}</span>
            <span className="field__hint">{t("onboarding.referenceHelp")}</span>
            <div className="segmented">
              <button type="button" aria-pressed={reference === "female"} onClick={() => setReference("female")}>
                {t("onboarding.female")}
              </button>
              <button type="button" aria-pressed={reference === "male"} onClick={() => setReference("male")}>
                {t("onboarding.male")}
              </button>
            </div>
          </div>
          <UnitNumberField
            id="onboarding-weight"
            label={t("onboarding.weightOptional")}
            min={1}
            unit={preferences.weightUnit}
            unitLabel={t("onboarding.weightUnit")}
            value={weight}
            onUnitChange={(unit) => updatePreferences({ weightUnit: unit as WeightUnit })}
            onValueChange={setWeight}
            options={[
              { label: "kg", value: "kg" },
              { label: "lb", value: "lb" }
            ]}
          />
          <SelectField
            label={t("onboarding.volumeUnit")}
            value={preferences.volumeUnit}
            onChange={(event) => updatePreferences({ volumeUnit: event.target.value as VolumeUnit })}
            options={[
              { label: "ml", value: "ml" },
              { label: "oz", value: "oz" }
            ]}
          />
          <SelectField
            label={t("onboarding.timezone")}
            value={preferences.timezoneMode}
            onChange={(event) => updatePreferences({ timezoneMode: event.target.value as "detected" | "fixed" })}
            options={[
              { label: t("onboarding.autoTimezone"), value: "detected" },
              { label: t("onboarding.fixedTimezone"), value: "fixed" }
            ]}
          />
          {preferences.timezoneMode === "fixed" ? (
            <InputField
              label={t("onboarding.fixedTimezoneLabel")}
              value={preferences.fixedTimezone}
              onChange={(event) => updatePreferences({ fixedTimezone: event.target.value })}
            />
          ) : (
            <p className="field__hint">{detectBrowserTimezone()}</p>
          )}
          <InputField
            label={t("onboarding.customGoalOptional", { unit: preferences.volumeUnit })}
            type="number"
            min={1}
            value={customGoal}
            onChange={(event) => setCustomGoal(event.target.value)}
          />
          <Button type="submit" disabled={pending}>
            {t("onboarding.saveContinue")}
          </Button>
        </form>
      </section>
    </main>
  );
}

type UnitNumberFieldProps = {
  id: string;
  label: string;
  value: string;
  unit: string;
  unitLabel: string;
  min?: number;
  required?: boolean;
  options: Array<{ label: string; value: string }>;
  onValueChange: (value: string) => void;
  onUnitChange: (value: string) => void;
};

function UnitNumberField({ id, label, value, unit, unitLabel, min, required, options, onValueChange, onUnitChange }: UnitNumberFieldProps) {
  return (
    <label className="field" htmlFor={id}>
      <span className="field__label">{label}</span>
      <span className="unit-input">
        <input
          className="input unit-input__value"
          id={id}
          min={min}
          onChange={(event) => onValueChange(event.target.value)}
          required={required}
          type="number"
          value={value}
        />
        <select aria-label={unitLabel} className="input select unit-input__unit" onChange={(event) => onUnitChange(event.target.value)} value={unit}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </span>
    </label>
  );
}

function readPositiveNumber(value: string): number | null {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

function readBirthYear(value: string): number | null {
  const number = Number(value);
  const currentYear = new Date().getFullYear();
  return Number.isInteger(number) && number >= 1900 && number <= currentYear ? number : null;
}

function formatOnboardingError(error: unknown, translateError: (code: string) => string, t: (key: string) => string): string {
  if (error instanceof ApiRequestError) {
    const requestSuffix = error.requestId ? ` (${t("common.requestId")}: ${error.requestId})` : "";
    return `${translateError(error.code)}${requestSuffix}`;
  }
  return translateError("INTERNAL_ERROR");
}
